import React, { useCallback, useRef } from 'react';
import { Grid } from '@mui/material';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import consts from '../consts';
import LoadingDialog from '../components/common/LoadingDialog';
import { useBreadcrumbs } from '../contexts/Breadcrumbs';
import { usePrerequisites } from '../contexts/Prerequisites';
import { useSetState, useUniqueEffect } from '../hooks';
import connectionService from '../services/Connection';
import util from '../util/index';
import AppConfigForm from './forms/AppConfigForm';

const AppConfig = ({ comboTypes = [
	'DateFormat',
	'LoadRelease',
	'LoadSegment',
	'TimeFormat',
	'PasswordStrength',
	'Topics',
	'MqttQOS',
	'DataPermissionTopic',
	'CommandPermissionTopic',
	'TelemetryPermissionTopic',
	'PubCommandPermissionTopic',
	'LogLevel',
	'DataTopics',
	'SyncInterval',
	'ApiLogLevel'
] }) => {
	const formRef = useRef(null)
	const { roles } = useSelector(storeState => storeState.auth);
	const diagnosticsUploadAccess = roles?.some?.(role => ['supervisor', 'superadmin', 'steris specialist', 'operator'].includes(role?.toLowerCase?.()));
	const { auth } = useSelector((storeState) => storeState);
	const Role = auth?.tags?.RoleName;
	const loggedin = auth?.loggedin;
	const isSuperVisior = loggedin && Role === 'Supervisor';
	const isOperator = loggedin && Role.match(/Operator/i);
	const isSterisSpecialist = loggedin && Role.match(/STERIS Specialist/i);
	const isSterisAdmin = loggedin && Role.match(/STERIS Admin/i);
	const location = useLocation();
	const inSetup = location.pathname?.toLowerCase?.() === '/setup';
	const url = inSetup ? connectionService.Config : connectionService.AppConfig;
	const actionValue = inSetup ? 'getConfig' : 'load'
	const [state, setState] = useSetState({
		loading: false,
		loadingMessage: '',
		activeRecord: null
	}
	);
	const [, setBreadcrumbs] = useBreadcrumbs();
	const [{ strings, session, LicenseValid }, { uploadLicense, getLicenseType }] = usePrerequisites();
	const navigate = useNavigate();
	function checkRestartAfterSubmit(submitUrl, values, formData, setStates, stateValues) {
		setStates({ loading: true });
		fetch(submitUrl, {
			method: 'POST',
			body: formData
		})
			.then((res) => res.json())
			.then(({ success, info, err }) => {
				setStates({ loading: false });

				if (success) {
					Swal.fire({
						titleText: strings.Success,
						text: strings.ConfigurationSaveSuccessText,
						icon: 'success',
						confirmButtonText: strings.Ok,
						allowOutsideClick: false
					});
					if (values.RunDBUpgrade) {
						Swal.fire({
							titleText: strings.Success,
							text: strings.successSeedingData,
							icon: 'success',
							confirmButtonText: strings.Ok,
							allowOutsideClick: false
						})
							.then(({ isConfirmed }) => {
								if (isConfirmed) {
									window.location.reload();
								}

							});
					}
					setStates((prevState) => ({
						activeRecord: {
							...prevState.activeRecord,
							record: { ...prevState?.activeRecord?.record, ...values }
						}
					}));
					if ((stateValues?.activeRecord?.record?.IsHttps || stateValues.activeRecord.record['Port'] !== values.Port) || (values?.SSLKeyFilePath && values?.SSLCertFilePath)) {
						window.location.href = (values?.SSLKeyFilePath && values?.SSLCertFilePath || stateValues?.activeRecord?.record?.IsHttps) ? `https://${window.location.hostname}:${values.Port}/${window.location.hash}` : `http://${window.location.hostname}:${values.Port}/${window.location.hash}`;
					} else {
						window.location.reload();
					}

				} else {
					Swal.fire({
						titleText: strings.ErrorTitle || 'Error',
						text: strings[info || err] || err || 'Some error occured during update',
						icon: 'error',
						confirmButtonText: strings.Ok,
					allowOutsideClick: false
				})
			}
		})
		.finally(() => setState({ loading: false }));
	}	
	const handleOnSubmit = useCallback(
		(values, { resetForm, status }) => {
			let reloadOnSave = false;
			const submitUrl = inSetup && state.activeRecord?.IsDBConnected ? connectionService.AppConfig : url;
			const hasServerCert = state.activeRecord?.record.hasServerCertFile;
			const hasServerKey = state.activeRecord?.record.hasServerKeyFile;
			const canSubmitBrokerSettings = (hasServerCert && hasServerKey) || (values.BrokerServerKey && values.BrokerServerCertificate);
			const licenseType = getLicenseType();
			for (const [topic, permissionField] of Object.entries(consts.topicPermissionsMap)) {
				if (values[permissionField].length && !values.MqttSubscribeTopic.includes(topic)) { // Appending SubscribeTopics which are not shown on screen
					values.MqttSubscribeTopic += values.MqttSubscribeTopic ? `,${topic}` : topic;
				}
			}
			if (inSetup && licenseType?.catsync && !canSubmitBrokerSettings) {
				Swal.fire({
					titleText: strings.Info || 'Info',
					text: strings.BrokerConfigRequired || 'Broker Configuration Required',
					icon: 'info',
					confirmButtonText: strings.Ok,
					allowOutsideClick: false
				});
				setState({ loading: false });
				return;
			}
			if (!licenseType?.catedge) {
				if (values.Port === 443 && (!values.SSLCertFilePath || !values.SSLKeyFilePath)) {
					Swal.fire({
						titleText: strings.Info || 'Info',
						text: strings.SSLNotification || 'SSL Key Path and SSL Cert Path required',
						icon: 'info',
						confirmButtonText: strings.Ok,
						allowOutsideClick: false
					});
					setState({ loading: false });
					return;
				}
				if (!values.UserName || !values.Password) {
					if (inSetup) {
						Swal.fire({
							titleText: strings.Info || 'Info',
							text: strings.UserNameAndPasswordRequired || 'User Name and Password required',
							icon: 'info',
							confirmButtonText: strings.Ok,
							allowOutsideClick: false
						});
						setState({ loading: false });
						return;
					}
				}
				if (!values.CatFeature || !values.CatPort || !values.ApiDbName) {
					Swal.fire({
						titleText: strings.Info || 'Info',
						text: strings.CATFeatureCATApiPortCATApiDbIsRequired,
						icon: 'info',
						confirmButtonText: strings.Ok,
						allowOutsideClick: false
					});
					setState({ loading: false });
					return;
				}
				if (
					!values.Email ||
					!/^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
						values.Email
					)
				) {
					if (inSetup) {
						Swal.fire({
							titleText: strings.Info || 'Info',
							text: strings.UserEmailAddressIsNotValid,
							icon: 'info',
							confirmButtonText: strings.Ok,
							allowOutsideClick: false
						});
						setState({ loading: false });
						return;
					}
				}
				if (inSetup) {

					if (values.PasswordStrength) {
						let passwordRegExp = consts.PasswordStrength.Strong;
						let passwordValidationError = strings.PasswordValidationError;

						state.activeRecord?.combos.PasswordStrength?.forEach?.(function (item) {
							if (item.LookupId === values.PasswordStrength) {
								passwordRegExp = item.CustomStringValue;
							}
						});
						passwordValidationError =
							passwordRegExp === consts.PasswordStrength.Easy
								? strings.PasswordEasyValidationError
								: passwordRegExp === consts.PasswordStrength.Medium
									? strings.PasswordMediumValidationError
									: strings.PasswordValidationError;
						if (!new RegExp(passwordRegExp).test(values?.Password)) {
							Swal.fire({
								titleText: strings.Info || 'Info',
								text: passwordValidationError,
								icon: 'info',
								confirmButtonText: strings.Ok,
								allowOutsideClick: false
							});
							setState({ loading: false });
							return;
						}
					}
				}
			}
			try {
				const currModules =
					typeof state.activeRecord?.record?.Modules === 'string'
						? JSON.parse(state.activeRecord?.record?.Modules)
						: state.activeRecord?.record?.Modules;
				const Modules = typeof values?.Modules === 'string' ? JSON.parse(values?.Modules) : values?.Modules;
				if (typeof currModules === 'object' && typeof Modules === 'object') {
					reloadOnSave = Object.entries(Modules).some(([key, value]) => {
						if(key === 'EndoScope'){
							return false;
						}
						const prevVal = currModules[key];
						const newVal = value;
						return prevVal !== newVal;
					});
				}
			} catch (error) {
				console.error('error', error);
			}
			if (values.Port === 443 && (!values.SSLCertFilePath || !values.SSLKeyFilePath)) {
				Swal.fire({
					titleText: strings.Info || 'Info',
					text: strings.SSLNotification || 'SSL Key Path and SSL Cert Path required',
					icon: 'info',
					confirmButtonText: strings.Ok,
					allowOutsideClick: false
				});
				setState({ loading: false });
				return;
			}


			const formData = new FormData();
			const allValues = {
				...values,
				ClientId: session?.clientId || state.activeRecord?.clientId || state.activeRecord?.record?.ClientId || 0
			};
			reloadOnSave =
				reloadOnSave ||
				(!inSetup && [
					'PasswordExpirationDays',
					'PasswordExpirationNotifyDays',
					'BIExpiredDays',
					'NetworkPath',
					'BINotificationTo',
					'FileNameFormat',
					'ReadFileInterval',
					'EquipmentFilePath',
					'LogLevel',
					'MaxLogsInDays',
					'CatViewerPath',
					'sessionTimeOutMins',
					'BrokerPort',
					'BrokerServerKey',
					'BrokerServerCertificate',
					'Persistance',
					'ClientInActiveInDays',
					'SMTPHost',
					'SMTPPort',
					'SMTPUserName',
					'SMTPPassword',
					'SMTPFrom',
					'SMTPTo',
					'SMTPSSL',
					'ArchiveFilePath',
					'ArchiveDayCount',
					'PurgeDayCount',
					'MongoDBLogsPath',
					'logsPurgeDayCount',
					'AdvFilesPurgeDayCount',
					'MqttHost',
					'MqttPort',
					'MqttClient',
					'MqttSubscribeTopic',
					'MqttDataPermissionTopic',
					'MqttTelemetryPermissionTopic',
					'MqttPublishTopic',
					'MqttPubDataPermission',
					'MqttCommandPermissionTopic',
					'MqttPubCommandPermissionTopic',
					'MqttQOS',
					'MqttSessionClean',
					'MacAddress',
					'payloadSize',
					'BrokerLogsCollectionPurgeDayCount',
					'MaxPublishCount',
					'AutoAuthorization',
					'CATReconnection',
					'CATReconnectionTime',
					'Name',
					'UploadURL',
					'ContentType',
					'AuthorizationToken',
					'CycleXMLFile',
					'CatService',
					'ApiDbName',
					'CatFeature',
					'CatPort',
					'IsCleanupService',
					'CleanupFrequency',
					'MaxCycleDays',
					'APIArchiveLocation',
					'TrackCycleCollectionPurgeDayCount',
					'VisionDataPurgeDayCount',
					'SyncInterval',
					'CatCloudURL',
					'FailSafeRestartMode',
					'webServerMinTls',
					'RunDBUpgrade'
				].some((field) => {
					if (consts.sensitiveKeys.includes(field) && !allValues[field]) {
						return false;
					}
					return allValues[field] !== state.activeRecord.record[field];
				}));
			delete allValues.LicenseFile;
			delete allValues.LicenseFileBtn;
			if (typeof allValues.Modules === 'object') {
				allValues.Modules = JSON.stringify(allValues.Modules);
			}

			for (const [key, value] of Object.entries(allValues)) {
				formData.append(key, value);
			}
			formData.append('action', 'save');
			if (location.pathname === '/setup') {
				formData.append('isSetup', true);
			}
			formData.append(
				'comboTypes',
				JSON.stringify([
					{ type: 'DateFormat', loaded: true },
					{ type: 'LoadRelease', loaded: true },
					{ type: 'LoadSegment', loaded: true },
					{ type: 'TimeFormat', loaded: true },
					{ type: 'PasswordStrength', loaded: true },
					{ type: 'Topics', loaded: true },
					{ type: 'MqttQOS', loaded: true },
					{ type: 'DataPermissionTopic', loaded: true },
					{ type: 'CommandPermissionTopic', loaded: true },
					{ type: 'PubCommandPermissionTopic', loaded: true },
					{ type: 'LogLevel', loaded: true },
					{ type: 'CATAPIFeature', loaded: true },
					{ type: 'DataTopics', loaded: true },
					{ type: 'SyncInterval', loaded: true },
				])
			);
			if (reloadOnSave) {
				util.showError(
					strings.ConfigurationChange,
					strings.Info,
					'info',
					() => checkRestartAfterSubmit(submitUrl, values, formData, setState, state)
				)
			}
			else {
				checkRestartAfterSubmit(submitUrl, values, formData, setState, state);
			}
		},
		[state.activeRecord?.record, state.activeRecord?.IsDBConnected, session?.clientId, strings]
	);



	const handleOnDBConnected = useCallback((json) => {
		window?.location?.reload?.();
	}, []);

	const getFormGroupsVisibility = useCallback(
		(json = {}) => {
			const { data = {}, IsDBConnected } = { ...json };
			const isDBConnected = IsDBConnected || data.IsDBConnected || Boolean(data?.DbUrl);
			const licenseType = getLicenseType();
			const isEndoAlone = licenseType?.products.length === 1 && licenseType?.products?.includes('catendo');
			const hasCATExport = licenseType?.products && licenseType?.products.includes('catexport');
			const hasCATExportAndEndo =
				licenseType?.products &&
				licenseType?.products.includes('catexport') &&
				licenseType?.products.includes('catendo');
			const noCATViewer = licenseType?.products && !licenseType?.products.includes('catviewer');
			const isCatEndo = licenseType?.products?.includes('catendo');
			let notSetupAndEndo = isEndoAlone && !inSetup;
			let isSetupAndEndo = isEndoAlone && inSetup;
			let isSetupAndCATExportPresent = hasCATExport && inSetup;
			let notSetupAndCATExportPresent = hasCATExport && !inSetup;
			const isBroker = licenseType.catsync;
			let formGroupsVisibility = new Map([
				['LicenseUpload', !isSuperVisior && !isSterisSpecialist && !isOperator],
				[
					'DatabaseConfiguration',
					notSetupAndEndo || notSetupAndCATExportPresent || isSterisSpecialist || isOperator ? false : !isDBConnected && inSetup && !isSuperVisior && !isSterisSpecialist
				],
				[
					'UserCreation',
					(isSetupAndEndo && noCATViewer && isDBConnected) ||
						(isSetupAndCATExportPresent && noCATViewer && isDBConnected)
						? true
						: isDBConnected && inSetup && !isSuperVisior && !isSterisSpecialist && !isOperator
				],
				[
					'ApplicationConfig',
					(isSetupAndEndo && noCATViewer) || (notSetupAndEndo && noCATViewer) || (isSuperVisior && noCATViewer) || (isSterisSpecialist || isOperator) ? false : isDBConnected
				],
				[
					'LoadSegmentConfigs',
					(isSetupAndEndo && noCATViewer) ||
						(notSetupAndEndo && noCATViewer) ||
						(hasCATExportAndEndo && noCATViewer) || (isSterisSpecialist || isOperator) || (noCATViewer && !hasCATExport)
						? false
						: isDBConnected
				],
				[
					'BrokerConfig',
					(isSetupAndEndo && noCATViewer) || (notSetupAndEndo && noCATViewer) || (isSterisSpecialist || isOperator)
						? false
						: isDBConnected && !isSuperVisior && isBroker
				],
				[
					'EmailConfig',
					(isSetupAndEndo && noCATViewer) || (notSetupAndEndo && noCATViewer) || (isSterisSpecialist || isOperator)
						? false
						: isDBConnected && !isSuperVisior
				],
				[
					'PurgeConfig',
					(isSetupAndEndo && noCATViewer) || (notSetupAndEndo && noCATViewer) || (isSterisSpecialist || isOperator) || (noCATViewer && !hasCATExport)
						? false
						: isDBConnected && !isSuperVisior
				],
				[
					'CATSync',
					(isSetupAndEndo && noCATViewer) || (notSetupAndEndo && noCATViewer) || (isSterisSpecialist || isOperator) || (noCATViewer && !hasCATExport)
						? false
						: isDBConnected && !isSuperVisior
				],
				[
					'CycleConfig',
					(isSetupAndEndo && noCATViewer) ||
						(notSetupAndEndo && noCATViewer) ||
						(hasCATExportAndEndo && noCATViewer) || (isSterisSpecialist || isOperator) || (noCATViewer && !hasCATExport)
						? false
						: isDBConnected
				],
				['CatApi', isEndoAlone || isSterisSpecialist || isOperator || (noCATViewer && !hasCATExport) ? false : isDBConnected && !isSuperVisior],
				['UploadAPIConfiguration', isEndoAlone || isSterisSpecialist || isOperator || (noCATViewer && !hasCATExport) ? false : isDBConnected && !isSuperVisior],
				[
					'CatCloudURL',
					(isEndoAlone && noCATViewer) || (hasCATExportAndEndo && noCATViewer) || (isSterisSpecialist || isOperator) || (noCATViewer && !hasCATExport)
						? false
						: isDBConnected && !isSuperVisior
				],
				['DiagnosticsUpload', isDBConnected && diagnosticsUploadAccess],
				['ScopeUpdate', isDBConnected && isSterisAdmin && isCatEndo],


			]);



			if (licenseType.catexport && !licenseType.multi) {
				formGroupsVisibility.set('BrokerConfig', false);
				formGroupsVisibility.set('CycleConfig', false);
				formGroupsVisibility.set('LoadSegmentConfigs', false);
				formGroupsVisibility.set('CatCloudURL', false);
			}
			if (licenseType.catexport && !licenseType.multi) {
				formGroupsVisibility.set('BrokerConfig', false);
				formGroupsVisibility.set('CycleConfig', false);
				formGroupsVisibility.set('LoadSegmentConfigs', false);
				formGroupsVisibility.set('CatCloudURL', false);
			}
			if (licenseType.catexport && !licenseType.multi) {
				formGroupsVisibility.set('BrokerConfig', false);
				formGroupsVisibility.set('CycleConfig', false);
				formGroupsVisibility.set('LoadSegmentConfigs', false);
				formGroupsVisibility.set('CatCloudURL', false);
			}
			if (licenseType.catsync && !licenseType.multi) {
				formGroupsVisibility.set('ApplicationConfig', isDBConnected);
				formGroupsVisibility.set('LoadSegmentConfigs', false);
				formGroupsVisibility.set('BrokerConfig', isDBConnected);
				formGroupsVisibility.set('EmailConfig', isDBConnected);
				formGroupsVisibility.set('PurgeConfig', false);
				formGroupsVisibility.set('CATSync', false);
				formGroupsVisibility.set('CycleConfig', false);
				formGroupsVisibility.set('CatApi', false);
				formGroupsVisibility.set('UploadAPIConfiguration', false);
				formGroupsVisibility.set('CatCloudURL', false);
			}

			if (licenseType.catexport && licenseType.catsync && licenseType?.products?.length === 2) {
				formGroupsVisibility.set('CycleConfig', false);
				formGroupsVisibility.set('LoadSegmentConfigs', false);
				formGroupsVisibility.set('CatCloudURL', false);
			}
			if (licenseType.catedge && !licenseType.multi) {
				formGroupsVisibility.set('ApplicationConfig', false);
				formGroupsVisibility.set('UserCreation', false);
				formGroupsVisibility.set('LoadSegmentConfigs', false);
				formGroupsVisibility.set('BrokerConfig', false);
				formGroupsVisibility.set('EmailConfig', false);
				formGroupsVisibility.set('PurgeConfig', false);
				formGroupsVisibility.set('CycleConfig', false);
				formGroupsVisibility.set('CatApi', false);
				formGroupsVisibility.set('UploadAPIConfiguration', false);
				formGroupsVisibility.set('CatCloudURL', isDBConnected);
			}
			if (licenseType.catviewer && !licenseType.multi) {
				formGroupsVisibility.set('BrokerConfig', false);
				formGroupsVisibility.set('CatCloudURL', false);
			} else if (licenseType.catviewer) {
				formGroupsVisibility.set('CatCloudURL', false);
			}
			if (!inSetup || !licenseType?.catsync) {
				formGroupsVisibility.set('BrokerConfigRequired', false);
			} else {
				formGroupsVisibility.set('BrokerConfigRequired', true);
			}
			return formGroupsVisibility;
		},
		[location.pathname, session, getLicenseType, diagnosticsUploadAccess, isSuperVisior, isSterisSpecialist]
	);
	const fetchData = useCallback(() => {
		setState({ loading: true });
		let appConfigId = util.localData.loginData?.tags?.AppConfigId;
		if (!consts.isNodeServer) {
			appConfigId = Number(appConfigId);
		}

		const formData = new FormData();
		formData.append('action', actionValue);
		formData.append(
			'comboTypes',
			JSON.stringify([
				{ type: 'DateFormat', loaded: true },
				{ type: 'LoadRelease', loaded: true },
				{ type: 'LoadSegment', loaded: true },
				{ type: 'TimeFormat', loaded: true },
				{ type: 'PasswordStrength', loaded: true },
				{ type: 'Topics', loaded: true },
				{ type: 'MqttQOS', loaded: true },
				{ type: 'DataPermissionTopic', loaded: true },
				{ type: 'CommandPermissionTopic', loaded: true },
				{ type: 'PubCommandPermissionTopic', loaded: true },
				{ type: 'TelemetryPermissionTopic', loaded: true },
				{ type: 'LogLevel', loaded: true },
				{ type: 'CATAPIFeature', loaded: true },
				{ type: 'DataTopics', loaded: true },
				{ type: 'SyncInterval', loaded: true },
				{ type: 'ApiLogLevel', loaded: true },
				{ type: "FailSafeRestartModes", loaded: true }
			])
		);
		formData.append('id', !consts.isNodeServer ? 1 : appConfigId);

		fetch(url, {
			method: 'POST',
			credentials: 'include',
			body: formData
		})
			.then((res) => res.json())
			.then((json) => {
				const { success, Success, data, combos, Lookup, ...rest } = json;
				const licenseType = getLicenseType();
				const appName = json?.appName || session?.tags?.LicenseAppName;
				const isCATSyncLicense = licenseType.catsync;
				const isCATViewerLicense = licenseType.catviewer;
				const isCATExportLicense = licenseType.catexport;
				const isCATEdgeLicense = licenseType.catedge;
				const isCATEdgeLicenseOnly = licenseType?.catedge && !licenseType?.multi;
				const isCATSyncLicenseOnly = licenseType?.catsync && !licenseType?.multi;

				const skipCaBundlePathRequired = !!data?.CaBundlePath;
				const formGroupsVisibility = getFormGroupsVisibility(json);
				if (success || Success) {
					if (formGroupsVisibility.get('BrokerConfig')) {
						if (!data.BrokerServerCertificate) {
							delete data.BrokerServerCertificate;
						}
						if (!data.BrokerServerKey) {
							delete data.BrokerServerKey;
						}
						delete data.CaBundlePath;
					}
					if (inSetup && formGroupsVisibility.get('UserCreation')) {
						delete data.Password;
						delete data.ConfirmUserPassword;
					}
					if (
						inSetup &&
						formGroupsVisibility.get('CATSync') &&
						!licenseType?.multi &&
						!licenseType?.catedge &&
						!licenseType?.catexport
					) {
						delete data.Password;
						delete data.ConfirmUserPassword;
					}

					setState({
						activeRecord: {
							IsDBConnected: data.IsDBConnected || false,
							skipCaBundlePathRequired,
							...rest,
							appName,
							success: true,
							combos: combos || Lookup,
							record: { ...data, EndoScope: data?.Modules?.EndoScope },
							formGroupsVisibility,
							isCATSyncLicense,
							isCATViewerLicense,
							isCATExportLicense,
							isCATEdgeLicense,
							isCATEdgeLicenseOnly,
							isCATSyncLicenseOnly,
							licenseType,
							inSetup,
							isSuperVisior,
							CaBundlePath: '',
							onDBConnected: handleOnDBConnected,
							onUploadLicence: uploadLicense
						},
						loading: false
					});

					if (json.redirectLogin) {
						navigate('/login');
					}
				}
			})
			.finally(() => setState({ loading: false }));
	}, [location.pathname, session, loggedin]);



	useUniqueEffect(() => {

		const titleStr =
			location.pathname === '/setup'
				? 'Configuration'
				: strings.AppConfig || 'System Configuration';;
		setBreadcrumbs({
			breadcrumbs: [{ text: titleStr }],
			docTitle: titleStr,
			hideHelp: location.pathname === '/setup'
		});
		fetchData();
	}, [])




	return (
		LicenseValid && (
			<Grid container>
				<Grid size={12}>
					<AppConfigForm
						activeRecord={state.activeRecord}
						onSubmit={handleOnSubmit}
						loading={state.loading}
						hideSubmit={!state.activeRecord?.IsDBConnected}
						hideCancel
						ref={formRef} />

					<LoadingDialog isOpen={state.loading} message={state.loadingMessage} />
				</Grid>
			</Grid>
		)
	);
};


export default AppConfig;
